import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, invalidatePrefix } from '../api/client';
import type { ApiKeyRow, ClientDetail as ClientDetailType, TransactionRow } from '../api/types';
import { Badge, StatusBadge } from '../components/Badge';
import { Card, KeyValue, PageHeader } from '../components/Card';
import { CopyButton } from '../components/CopyButton';
import { ArrowLeft } from 'lucide-react';
import { ErrorBanner, LoadingBlock, WarningBanner } from '../components/Feedback';
import { Button, Field, Input, Select, Textarea, Toggle } from '../components/Form';
import { Modal } from '../components/Modal';
import { DataTable, Mono } from '../components/Table';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { ago, dateTime, money, num, text } from '../lib/format';

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const path = `/admin-api/clients/${id}`;
  const query = useQuery<ClientDetailType, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get<ClientDetailType>(path, signal),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
  });

  const [editOpen, setEditOpen] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [keyOpen, setKeyOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('default');
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  // Edit form
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editStatus, setEditStatus] = useState<'active' | 'suspended'>('active');
  const [editRateLimit, setEditRateLimit] = useState('120');
  const [editMaxConcurrent, setEditMaxConcurrent] = useState('50');
  const [editAllowNegative, setEditAllowNegative] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Topup form
  const [topupAmount, setTopupAmount] = useState('0');
  const [topupNote, setTopupNote] = useState('');
  const [topupType, setTopupType] = useState<'topup' | 'adjustment'>('topup');
  const [topupBusy, setTopupBusy] = useState(false);

  const c = query.data;

  const openEdit = () => {
    if (!c) return;
    setEditName(c.client.name);
    setEditEmail(c.client.email ?? '');
    setEditStatus(c.client.status);
    setEditRateLimit(String(c.client.rate_limit_per_min));
    setEditMaxConcurrent(String(c.client.max_concurrent_jobs));
    setEditAllowNegative(c.client.allow_negative);
    setEditNotes(c.client.notes ?? '');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin-api/clients/${id}`, {
        name: editName.trim(),
        email: editEmail.trim() || null,
        status: editStatus,
        rate_limit_per_min: Number(editRateLimit) || 120,
        max_concurrent_jobs: Number(editMaxConcurrent) || 50,
        allow_negative: editAllowNegative,
        notes: editNotes.trim() || null,
      });
      toast.success('Клиент обновлён');
      setEditOpen(false);
      void invalidatePrefix(queryClient, '/admin-api/clients');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const doTopup = async () => {
    setTopupBusy(true);
    try {
      const result = await api.post<{ balance: number }>(`/admin-api/clients/${id}/topup`, {
        amount: Number(topupAmount) || 0,
        note: topupNote.trim() || undefined,
        type: topupType,
      });
      toast.success(`Баланс: ${money(result.balance)}`);
      setTopupOpen(false);
      setTopupAmount('0'); setTopupNote('');
      void invalidatePrefix(queryClient, '/admin-api/clients');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setTopupBusy(false);
    }
  };

  const createKey = async () => {
    setKeyOpen(false);
    try {
      const result = await api.post<{ id: number; name: string; api_key: string }>(`/admin-api/clients/${id}/keys`, { name: newKeyName.trim() || 'key' });
      setCreatedKey(result.api_key);
      toast.success('Ключ создан');
      void invalidatePrefix(queryClient, '/admin-api/clients');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  const revokeKey = async (key: ApiKeyRow) => {
    const ok = await confirm({
      title: 'Отозвать ключ?',
      body: `Ключ «${key.name}» (${key.prefix}…) будет отозван.`,
      confirmLabel: 'Отозвать',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/admin-api/clients/${id}/keys/${key.id}`);
      toast.success('Ключ отозван');
      void invalidatePrefix(queryClient, '/admin-api/clients');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  if (query.isLoading) return <LoadingBlock />;
  if (query.error) return <ErrorBanner message={query.error.message} onRetry={() => query.refetch()} />;
  if (!c) return null;

  return (
    <div className="space-y-4">
      <PageHeader
        title={c.client.name}
        description={`ID: ${c.client.id}`}
        actions={
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => setTopupOpen(true)}>Пополнить</Button>
            <Button variant="secondary" onClick={openEdit}>Редактировать</Button>
            <Link to="/clients">
              <Button variant="ghost" icon={<ArrowLeft className="size-3.5" />}>К списку</Button>
            </Link>
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Профиль">
          <KeyValue
            items={[
              { label: 'Статус', value: <StatusBadge kind="client" status={c.client.status} /> },
              { label: 'Email', value: text(c.client.email) },
              { label: 'Баланс', value: <span className={c.client.balance < 0 ? 'text-red-400' : ''}>{money(c.client.balance)}</span> },
              { label: 'Зарезервировано', value: money(c.client.reserved) },
              { label: 'Доступно', value: money(c.client.available) },
              { label: 'Отрицательный баланс', value: c.client.allow_negative ? 'да' : 'нет' },
              { label: 'Лимит / мин', value: num(c.client.rate_limit_per_min) },
              { label: 'Макс. задач', value: num(c.client.max_concurrent_jobs) },
              { label: 'Создан', value: dateTime(c.client.created_at) },
              { label: 'Заметки', value: text(c.client.notes) },
            ]}
          />
        </Card>

        <Card title="Статистика">
          <KeyValue
            items={[
              { label: 'Покупок всего', value: num(c.stats.purchases) },
              { label: 'Выдано', value: num(c.stats.delivered) },
              { label: 'Ошибок', value: num(c.stats.failed) },
              { label: 'Выручка', value: money(c.stats.revenue) },
              { label: 'Себестоимость', value: money(c.stats.cost) },
              { label: 'Маржа', value: money(c.stats.margin) },
            ]}
          />
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card
          title="API-ключи"
          actions={<Button size="sm" variant="primary" onClick={() => setKeyOpen(true)}>Создать ключ</Button>}
          noPadding
        >
          <DataTable<ApiKeyRow>
            columns={[
              { key: 'name', header: 'Имя', render: (r) => <span className="truncate">{r.name}</span> },
              { key: 'prefix', header: 'Префикс', render: (r) => <Mono>{r.prefix}…</Mono> },
              { key: 'revoked', header: 'Статус', width: '5rem', render: (r) => r.revoked ? <Badge tone="muted">отозван</Badge> : <Badge tone="success">активен</Badge> },
              { key: 'last_used', header: 'Использован', align: 'right', hide: 'sm', render: (r) => r.last_used_at ? ago(r.last_used_at) : '—' },
              { key: 'actions', header: '', width: '5rem', align: 'right', render: (r) => r.revoked ? null : <Button size="sm" variant="ghost" onClick={() => revokeKey(r)}>Отозвать</Button> },
            ]}
            rows={c.keys}
            rowKey={(r) => r.id}
            emptyTitle="Ключей нет"
            minWidth="22rem"
          />
        </Card>

        <Card title="Транзакции" noPadding>
          <DataTable<TransactionRow>
            columns={[
              { key: 'type', header: 'Тип', width: '6rem', render: (r) => <Badge tone={r.type === 'charge' ? 'neutral' : r.type === 'topup' ? 'success' : r.type === 'refund' ? 'warning' : 'info'}>{r.type}</Badge> },
              { key: 'amount', header: 'Сумма', align: 'right', render: (r) => <span className={r.amount < 0 ? 'text-red-400' : 'text-emerald-400'}>{money(r.amount)}</span> },
              { key: 'balance_after', header: 'После', align: 'right', hide: 'sm', render: (r) => money(r.balance_after) },
              { key: 'note', header: 'Заметка', hide: 'md', render: (r) => <span className="truncate">{text(r.note)}</span> },
              { key: 'created_at', header: 'Дата', align: 'right', width: '7rem', render: (r) => ago(r.created_at) },
            ]}
            rows={c.transactions}
            rowKey={(r) => r.id}
            emptyTitle="Транзакций нет"
            minWidth="24rem"
          />
        </Card>
      </div>

      {/* Service access (per-key project whitelist) */}
      <AccessCard clientId={Number(id)} />

      {/* Country access (per-key category whitelist) */}
      <CategoryAccessCard clientId={Number(id)} />

      {/* Edit modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Редактировать клиента" size="md"
        footer={<><Button variant="ghost" onClick={() => setEditOpen(false)}>Отмена</Button><Button variant="primary" loading={saving} onClick={saveEdit}>Сохранить</Button></>}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Имя" className="sm:col-span-2"><Input value={editName} onChange={(e) => setEditName(e.target.value)} /></Field>
          <Field label="Email"><Input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="email@example.com" /></Field>
          <Field label="Статус">
            <Select value={editStatus} onChange={(e) => setEditStatus(e.target.value as 'active' | 'suspended')}>
              <option value="active">активен</option>
              <option value="suspended">заблокирован</option>
            </Select>
          </Field>
          <Field label="Лимит / мин"><Input value={editRateLimit} onChange={(e) => setEditRateLimit(e.target.value)} inputMode="numeric" /></Field>
          <Field label="Макс. задач"><Input value={editMaxConcurrent} onChange={(e) => setEditMaxConcurrent(e.target.value)} inputMode="numeric" /></Field>
          <Field label="Заметки" className="sm:col-span-2"><Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} /></Field>
          <Toggle checked={editAllowNegative} onChange={setEditAllowNegative} label="Разрешить отрицательный баланс" />
        </div>
      </Modal>

      {/* Topup modal */}
      <Modal open={topupOpen} onClose={() => setTopupOpen(false)} title="Пополнение баланса" size="sm"
        footer={<><Button variant="ghost" onClick={() => setTopupOpen(false)}>Отмена</Button><Button variant="primary" loading={topupBusy} onClick={doTopup}>Пополнить</Button></>}
      >
        <div className="grid gap-3">
          <Field label="Тип">
            <Select value={topupType} onChange={(e) => setTopupType(e.target.value as 'topup' | 'adjustment')}>
              <option value="topup">Пополнение</option>
              <option value="adjustment">Корректировка</option>
            </Select>
          </Field>
          <Field label="Сумма ($)" hint="Отрицательное число для списания">
            <Input value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} inputMode="decimal" />
          </Field>
          <Field label="Заметка"><Input value={topupNote} onChange={(e) => setTopupNote(e.target.value)} /></Field>
        </div>
      </Modal>

      {/* Create key name modal */}
      <Modal open={keyOpen} onClose={() => setKeyOpen(false)} title="Создать API-ключ" size="sm"
        footer={<><Button variant="ghost" onClick={() => setKeyOpen(false)}>Отмена</Button><Button variant="primary" onClick={createKey}>Создать</Button></>}
      >
        <Field label="Имя ключа"><Input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} /></Field>
      </Modal>

      {/* Created key modal */}
      <Modal open={createdKey !== null} onClose={() => setCreatedKey(null)} title="Ключ создан" size="sm"
        footer={<Button variant="primary" onClick={() => setCreatedKey(null)}>Понятно</Button>}
      >
        <div className="space-y-3">
          <WarningBanner title="Ключ показан один раз" message="Сохраните ключ — после закрытия он недоступен." />
          <div className="flex items-center gap-2">
            <Mono className="flex-1 break-all text-[11px]">{createdKey}</Mono>
            <CopyButton value={createdKey ?? ''} label="Копировать" />
          </div>
        </div>
      </Modal>
    </div>
  );
}


/** Как этот ключ будет видеть одну позицию. Пусто — общее название. */
function RenameModal({
  target,
  onClose,
  onSave,
}: {
  target: { id: number; label: string; original: string; current: string | null } | null;
  onClose: () => void;
  onSave: (id: number, name: string | null) => void;
}) {
  const [value, setValue] = useState('');
  useEffect(() => setValue(target?.current ?? ''), [target]);
  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title="Название для этого клиента"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button variant="primary" onClick={() => target && onSave(target.id, value.trim() || null)}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-zinc-400">
          Это имя увидит только этот ключ — вместо общего. Пустое поле возвращает
          общее название из каталога.
        </p>
        <Field label="У поставщика">
          <Input value={target?.original ?? ''} readOnly />
        </Field>
        <Field label="Сейчас показывается">
          <Input value={target?.label ?? ''} readOnly />
        </Field>
        <Field label="Название для клиента">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Например: Google почта"
            onKeyDown={(e) => e.key === 'Enter' && target && onSave(target.id, value.trim() || null)}
          />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- access card

type AccessRow = { id: number; name: string; name_original: string; own_name: string | null; type: number; stock: number; enabled: boolean };
type AccessState = { restrict: boolean; total: number; projects: AccessRow[] };
type CatalogPickRow = { id: number; name: string; type: number; stock: number; price: number };

function AccessCard({ clientId }: { clientId: number }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const path = `/admin-api/clients/${clientId}/access`;
  const query = useQuery<AccessState, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get<AccessState>(path, signal),
    retry: 1,
  });
  const [search, setSearch] = useState('');
  const [picks, setPicks] = useState<CatalogPickRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState<{ id: number; label: string; original: string; current: string | null } | null>(null);

  const run = async (body: Record<string, unknown>, okMsg: string) => {
    setBusy(true);
    try {
      await api.put(path, body);
      toast.success(okMsg);
      setPicks([]);
      setSearch('');
      // Без этого чипы остаются прежними до перезагрузки страницы, и кажется,
      // что кнопка ничего не сделала.
      await invalidatePrefix(queryClient, path);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const doSearch = async () => {
    if (search.trim().length < 2) return;
    try {
      const res = await api.get<{ list: CatalogPickRow[] }>(
        `/admin-api/catalog/projects?search=${encodeURIComponent(search.trim())}&limit=8&include_disabled=0`,
      );
      setPicks(res.list);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка поиска');
    }
  };

  const a = query.data;

  return (
    <Card
      title="Доступ к сервисам"
      actions={
        <Toggle
          checked={a?.restrict ?? false}
          onChange={(v) => a && void run({ restrict: v }, v ? 'Ограничение включено' : 'Ограничение снято: доступны все сервисы')}
          label="Только белый список"
        />
      }
    >
      {query.isError && <ErrorBanner title="Не удалось загрузить доступ" message={query.error?.message ?? 'Ошибка'} />}
      {!a ? (
        <LoadingBlock />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-zinc-400">
            {a.restrict
              ? `Ключу доступны только выбранные сервисы (${a.total} шт.). Покупка остальных вернёт 403.`
              : 'Сейчас ключу доступен весь каталог. Включите ограничение, чтобы разрешить только выбранные сервисы. Добавлять сюда можно и без ограничения — тогда строка нужна только ради своего названия (✎).'}
          </p>

          <div className="flex flex-wrap gap-1.5">
            {a.projects.length === 0 && <span className="text-sm text-zinc-500">Список пуст</span>}
            {a.projects.map((p) => (
              <span key={p.id} className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-800/60 px-2 py-1 text-xs">
                <span className="truncate" title={p.name_original}>{p.name}</span>
                {p.own_name && <span className="text-sky-400" title="Название задано для этого клиента">✎</span>}
                <span className="text-zinc-500">t{p.type}</span>
                <button
                  className="text-zinc-400 hover:text-sky-400"
                  title="Как называть для этого клиента"
                  onClick={() => setRenaming({ id: p.id, label: p.name, original: p.name_original, current: p.own_name })}
                >
                  ✎
                </button>
                <button
                  className="text-zinc-400 hover:text-red-400"
                  disabled={busy}
                  onClick={() => void run({ remove: [p.id] }, 'Сервис удалён из доступа')}
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <div className="flex gap-2">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Поиск сервиса для добавления (название)" onKeyDown={(e) => e.key === 'Enter' && void doSearch()} />
            <Button variant="secondary" onClick={() => void doSearch()}>Найти</Button>
          </div>

          {picks.length > 0 && (
            <div className="space-y-1">
              {picks.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 rounded-md border border-zinc-800 px-2 py-1.5 text-sm">
                  <span className="min-w-0 truncate">{p.name} <span className="text-zinc-500">· type {p.type} · {money(p.price)} · stock {num(p.stock)}</span></span>
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => void run({ add: [p.id] }, 'Сервис добавлен')}>+ Добавить</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <RenameModal
        target={renaming}
        onClose={() => setRenaming(null)}
        onSave={(id, name) => {
          setRenaming(null);
          void run({ rename: { id, name } }, name ? 'Название сохранено' : 'Вернули общее название');
        }}
      />
    </Card>
  );
}

// -------------------------------------------------------- country access card

type CategoryRow = { id: number; name: string; name_original: string; own_name: string | null; iso2: string | null; enabled: boolean };
type CategoryAccessState = { restrict: boolean; total: number; categories: CategoryRow[] };
type CategoryPickRow = { id: number; name: string; iso2: string | null; projects: number };

/** Turns "US" into 🇺🇸 — regional indicators, so no flag images are needed. */
export function flagOf(iso2: string | null | undefined): string {
  const code = (iso2 ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '🏳️';
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

function CategoryAccessCard({ clientId }: { clientId: number }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const path = `/admin-api/clients/${clientId}/category-access`;
  const query = useQuery<CategoryAccessState, ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) => api.get<CategoryAccessState>(path, signal),
    retry: 1,
  });
  const all = useQuery<{ list: CategoryPickRow[] }, ApiError>({
    queryKey: ['api', '/admin-api/catalog/categories'],
    queryFn: ({ signal }) => api.get<{ list: CategoryPickRow[] }>('/admin-api/catalog/categories', signal),
    retry: 1,
  });
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState<{ id: number; label: string; original: string; current: string | null } | null>(null);

  const run = async (body: Record<string, unknown>, okMsg: string) => {
    setBusy(true);
    try {
      await api.put(path, body);
      toast.success(okMsg);
      await invalidatePrefix(queryClient, path);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const a = query.data;
  const chosen = new Set((a?.categories ?? []).map((c) => c.id));
  const rest = (all.data?.list ?? []).filter((c) => !chosen.has(c.id));

  return (
    <Card
      title="Доступ к странам"
      actions={
        <Toggle
          checked={a?.restrict ?? false}
          onChange={(v) => a && void run({ restrict: v }, v ? 'Ограничение включено' : 'Ограничение снято: доступны все страны')}
          label="Только белый список"
        />
      }
    >
      {query.isError && <ErrorBanner title="Не удалось загрузить доступ к странам" message={query.error?.message ?? 'Ошибка'} />}
      {!a ? (
        <LoadingBlock />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-zinc-400">
            {a.restrict
              ? `Ключу доступны только выбранные страны (${a.total} шт.). Покупка остальных вернёт 403.`
              : 'Сейчас ключу доступны все страны. Ограничение по странам не зависит от ограничения по сервисам: можно открыть весь каталог, но только в двух странах.'}
          </p>

          <div className="flex flex-wrap gap-1.5">
            {a.categories.length === 0 && <span className="text-sm text-zinc-500">Список пуст</span>}
            {a.categories.map((c) => (
              <span key={c.id} className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-800/60 px-2 py-1 text-xs">
                <span>{flagOf(c.iso2)}</span>
                <span className="truncate" title={c.name_original}>{c.name}</span>
                {c.iso2 && <span className="text-zinc-500">{c.iso2}</span>}
                <button
                  className="text-zinc-400 hover:text-sky-400"
                  title="Как называть для этого клиента"
                  onClick={() => setRenaming({ id: c.id, label: c.name, original: c.name_original, current: c.own_name })}
                >
                  ✎
                </button>
                <button
                  className="text-zinc-400 hover:text-red-400"
                  disabled={busy}
                  onClick={() => void run({ remove: [c.id] }, 'Страна убрана из доступа')}
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          {rest.length > 0 && (
            <div className="flex flex-wrap gap-1.5 border-t border-zinc-800 pt-3">
              {rest.map((c) => (
                <Button key={c.id} size="sm" variant="secondary" disabled={busy} onClick={() => void run({ add: [c.id] }, 'Страна добавлена')}>
                  {flagOf(c.iso2)} {c.name} <span className="text-zinc-500">· {c.projects}</span>
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
      <RenameModal
        target={renaming}
        onClose={() => setRenaming(null)}
        onSave={(id, name) => {
          setRenaming(null);
          void run({ rename: { id, name } }, name ? 'Название сохранено' : 'Вернули общее название');
        }}
      />
    </Card>
  );
}
